import { Link, useLocation, useNavigate } from "react-router-dom";
import { BRAND } from "@/lib/brand";
import { useAuth } from "@/context/AuthContext";
import { LogOut, ChevronRight } from "lucide-react";

/**
 * Shared shell for admin pages. Provides top bar with logo, welcome, logout,
 * and a breadcrumb.
 */
export default function AdminShell({ children, breadcrumbs = [], step }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();

  const onLogout = () => {
    logout();
    nav("/admin/login", { replace: true });
  };

  return (
    <div
      data-testid="admin-shell"
      className="min-h-screen canvas-soft flex flex-col"
    >
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-slate-100">
        <div className="mx-auto max-w-7xl px-6 md:px-8 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Link to="/admin" data-testid="admin-logo" className="flex items-center gap-3">
              <img
                src={BRAND.logo}
                alt="Sky Basket"
                className="w-10 h-10 rounded-full ring-2 ring-white shadow"
              />
              <div className="hidden sm:flex flex-col leading-tight">
                <span className="font-display font-black text-slate-900">
                  Sky Basket
                </span>
                <span className="text-[10px] uppercase tracking-[0.22em] text-[#cb05a5] font-semibold">
                  Admin Portal
                </span>
              </div>
            </Link>
          </div>

          <div className="hidden md:flex items-center gap-2 text-sm text-slate-500">
            {breadcrumbs.length > 0 && (
              <nav
                data-testid="admin-breadcrumbs"
                className="flex items-center gap-1"
              >
                <Link
                  to="/admin"
                  className="hover:text-[#299fe0] transition-colors"
                >
                  Dashboard
                </Link>
                {breadcrumbs.map((b, i) => (
                  <span key={i} className="inline-flex items-center gap-1">
                    <ChevronRight size={14} className="text-slate-300" />
                    {b.href ? (
                      <Link
                        to={b.href}
                        className="hover:text-[#299fe0] transition-colors"
                      >
                        {b.label}
                      </Link>
                    ) : (
                      <span className="text-slate-800 font-medium">
                        {b.label}
                      </span>
                    )}
                  </span>
                ))}
              </nav>
            )}
          </div>

          <div className="flex items-center gap-3">
            <div className="hidden sm:block text-right">
              <div className="text-xs text-slate-500">Signed in as</div>
              <div className="text-sm font-semibold text-slate-800">
                {user?.name || user?.email}
              </div>
            </div>
            <button
              type="button"
              onClick={onLogout}
              data-testid="logout-button"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-slate-200 hover:border-slate-900 hover:text-slate-900 text-slate-600 text-sm font-semibold transition-colors"
            >
              <LogOut size={14} />
              Logout
            </button>
          </div>
        </div>

        {step && (
          <div className="border-t border-slate-100 bg-white">
            <div className="mx-auto max-w-7xl px-6 md:px-8 py-3 flex items-center gap-3">
              <StepDot active={step >= 1} label="Step 1" />
              <div className="flex-1 h-[2px] bg-slate-100 relative">
                <div
                  className="absolute inset-y-0 left-0 transition-all"
                  style={{
                    width: step >= 2 ? "100%" : "0%",
                    background:
                      "linear-gradient(90deg, #299fe0, #cb05a5)",
                  }}
                />
              </div>
              <StepDot active={step >= 2} label="Step 2" />
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>
    </div>
  );
}

function StepDot({ active, label }) {
  return (
    <div className="flex items-center gap-2">
      <div
        className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold ${
          active
            ? "text-white shadow"
            : "text-slate-400 bg-slate-100"
        }`}
        style={
          active
            ? {
                background:
                  "linear-gradient(135deg, #299fe0, #cb05a5)",
              }
            : undefined
        }
      >
        {label.split(" ")[1]}
      </div>
      <span
        className={`text-xs font-semibold uppercase tracking-[0.15em] ${
          active ? "text-slate-900" : "text-slate-400"
        }`}
      >
        {label}
      </span>
    </div>
  );
}
